import { Button, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type DepartmentItem = { id: string; name: string };
type PositionItem = { id: string; name: string };
type SOPItem = { id: string; name: string };

const FlowMappingFormSchema = z.object({
  sopId: z.string().nullable().optional(),
  positionId: z.string().nullable().optional(),
  departmentId: z.string().nullable().optional(),
});
export type FlowMappingFormValues = z.infer<typeof FlowMappingFormSchema>;

export function FlowMappingForm({
  initialValues,
  onSubmit,
  submitting,
  flowDepartmentId,
}: {
  initialValues?: Partial<FlowMappingFormValues>;
  onSubmit: (values: FlowMappingFormValues) => void;
  submitting?: boolean;
  flowDepartmentId?: string | null; // Filter lookups by flow's department
}) {
  const methods = RHF.useForm<FlowMappingFormValues>({
    resolver: zodResolver(FlowMappingFormSchema),
    defaultValues: {
      sopId: null,
      positionId: null,
      departmentId: null,
      // Auto-select flow's department if not provided in initialValues
      ...(initialValues?.departmentId === undefined && flowDepartmentId
        ? { departmentId: flowDepartmentId }
        : {}),
      ...initialValues,
    },
  });

  // Watch form values for preview
  const sopId = RHF.useWatch({ control: methods.control, name: "sopId" });
  const positionId = RHF.useWatch({
    control: methods.control,
    name: "positionId",
  });
  const departmentId = RHF.useWatch({
    control: methods.control,
    name: "departmentId",
  });

  // Fetch names for selected items
  const { data: sopItem } = useQuery({
    queryKey: ["flows-sops", "preload", sopId],
    queryFn: () =>
      hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/flows/sops/lookup`,
        sopId ?? "",
      ),
    enabled: !!sopId,
  });

  const { data: positionItem } = useQuery({
    queryKey: ["flows-positions", "preload", positionId],
    queryFn: async () => {
      const item = await hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/flows/positions/lookup`,
        positionId ?? "",
      );
      if (!item) return null;
      return { id: item.id, name: item.name };
    },
    enabled: !!positionId,
  });

  const { data: departmentItem } = useQuery({
    queryKey: ["flows-departments", "preload", departmentId],
    queryFn: () =>
      hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/flows/departments/lookup`,
        departmentId ?? "",
      ),
    enabled: !!departmentId,
  });

  // Determine if preview should be shown
  const hasMapping = !!(sopId || positionId || departmentId);

  return (
    <FormRoot<FlowMappingFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInfiniteCombobox<DepartmentItem>
        name="departmentId"
        label="ພະແນກ"
        queryKey={["flows-departments"]}
        queryFn={(args) =>
          fetchLookupForInfinite(
            `${config.apiUrl}/flows/departments/lookup`,
            args,
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/flows/departments/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ພະແນກ"
        clearable={!flowDepartmentId}
        disabled={!!flowDepartmentId}
      />

      <FormInfiniteCombobox<PositionItem>
        name="positionId"
        label="ຕໍາແໜ່ງ"
        queryKey={[
          "flows-positions",
          ...(flowDepartmentId ? [flowDepartmentId] : []),
        ]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/flows/positions/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (flowDepartmentId) {
            url.searchParams.set("departmentId", flowDepartmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return {
            items: items.map((item: { id: string; name: string }) => ({
              id: item.id,
              name: item.name,
            })),
            nextPage,
          };
        }}
        preloadQueryFn={async (id) => {
          const item = await hydrateLookupItem<{ id: string; name: string }>(
            `${config.apiUrl}/flows/positions/lookup`,
            id,
          );
          if (!item) return null;
          return { id: item.id, name: item.name };
        }}
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
        clearable
      />

      <FormInfiniteCombobox<SOPItem>
        name="sopId"
        label="SOP"
        queryKey={[
          "flows-sops",
          ...(flowDepartmentId ? [flowDepartmentId] : []),
          ...(positionId ? [positionId] : []),
        ]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/flows/sops/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (flowDepartmentId) {
            url.searchParams.set("departmentId", flowDepartmentId);
          }
          if (positionId) {
            url.searchParams.set("positionId", positionId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/flows/sops/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກ SOP (ບໍ່ບັງຄັບ)"
        clearable
      />

      {/* Real-time Preview Section */}
      {hasMapping && (
        <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
          <h4 className="font-semibold text-sm">ຕົວຢ່າງ: Flow ນີ້ຈະຜູກກັບກັບ:</h4>
          <div className="space-y-1 text-sm">
            {sopItem && (
              <div>
                <span className="font-medium text-muted-foreground">SOP:</span>
                <span className="ml-2">{sopItem.name}</span>
              </div>
            )}
            {(positionItem || departmentItem) && (
              <div>
                <span className="font-medium text-muted-foreground">
                  ພະແນກ/ຕໍາແໜ່ງ:
                </span>
                <ul className="mt-1 ml-2 list-inside list-disc">
                  {departmentItem && <li>ພະແນກ: {departmentItem.name}</li>}
                  {positionItem && <li>ຕໍາແໜ່ງ: {positionItem.name}</li>}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
